import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import { fetchJsonWithAuth } from './fetch-json.util';
import { PhasesTags } from '../components/admin/Templates/PhasesTags';
import { useAPIProgress } from './APIProgress';
import { useStatusMessages } from './StatusMessages';
import MessageBanner from './MessageBanner';
import {
  transformPhasesForPersistence,
  transformPhasesFromAPIResponse
} from './UtilFunctions';
import SMModal from './SMModal';
import { COMMENTS_TITLE, LINKS_TITLE, TAG_TO_PHASE } from './AppConstants';

export function EditEntityModal({
  showModal,
  handleCancel,
  entityToEdit,
  setEntityToEdit,
  handleEntityUpdateLogic,
  entityName,
  dispatch,
  url,
  entityType
}) {
  const MAX_DESCRIPTION_LENGTH = 512;
  const [description, setDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [originalLinkUrl, setOriginalLinkUrl] = useState('');

  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();
  const [showDirtyModal, setShowDirtyModal] = useState(false);
  const handleDirtyModalCancel = () => setShowDirtyModal(false);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [originalPhases, setOrignialPhases] = useState([]);
  const {
    statusMessages,
    clearAllMessages,
    setStatusInfoMsg,
    setStatusErrorMsg,
    setStatusSuccessMsg
  } = useStatusMessages();

  const resolvedEntityType =
    entityType ||
    (entityToEdit?.linkId
      ? 'link'
      : entityToEdit?.commentId
        ? 'comment'
        : 'attachment');
  const isCommentEntity = resolvedEntityType === 'comment';
  const isLinkEntity = resolvedEntityType === 'link';

  function isFormDirty() {
    const phasesChanged =
      JSON.stringify(originalPhases) !== JSON.stringify(selectedPhases);
    const descriptionChanged = originalDescription !== description;
    const urlChanged = isLinkEntity && originalLinkUrl !== linkUrl;

    return phasesChanged || descriptionChanged || urlChanged;
  }

  function cancelAction() {
    if (isFormDirty()) setShowDirtyModal(true);
    else handleCancel();
  }

  const cancelDirtyOKAction = () => {
    setShowDirtyModal(false);
    handleCancel();
  };

  useEffect(() => {
    clearAllMessages();
  }, [showModal, clearAllMessages]);

  const handleSave = () => {
    if (!entityToEdit) {
      return;
    }

    const entityValueToShow =
      entityToEdit?.attachment ||
      entityToEdit?.fileName ||
      entityToEdit?.url ||
      entityName;

    if (!isFormDirty()) {
      setStatusInfoMsg('No changes made to ' + entityValueToShow + '.');
      return;
    }

    clearAllMessages();
    incrementAPIInProgress();

    const entityToUpdate = {
      ...entityToEdit,
      ...transformPhasesForPersistence(selectedPhases)
    };

    if (isCommentEntity) {
      entityToUpdate.comment = description;
    } else {
      entityToUpdate.description = description;
    }

    if (isLinkEntity) {
      entityToUpdate.url = linkUrl;
    }

    const body = JSON.stringify(entityToUpdate);

    if (handleEntityUpdateLogic) {
      Promise.resolve(handleEntityUpdateLogic(entityToUpdate))
        .then((response) => {
          if (response?.type?.endsWith('/fulfilled')) {
            if (response.payload) {
              setEntityToEdit(response.payload);
            }
            setStatusSuccessMsg(`${entityName} saved successfully.`);
            return;
          }

          setStatusErrorMsg(
            `Couldn't update the ${entityName}. Please try again.`
          );
        })
        .catch((error) => {
          setStatusErrorMsg(
            `Couldn't update the ${entityName}. Please try again.`,
            error
          );
        })
        .finally(() => {
          decrementAPIInProgress();
        });
      return;
    }

    fetchJsonWithAuth(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body
    })
      .then((response) => {
        const responseEntity = response.json;

        setEntityToEdit(responseEntity);
        if (response.meta.status === 200) {
          setStatusSuccessMsg(
            `${
              responseEntity?.fileName || responseEntity.attachment
            } has been successfully updated.`
          );
          dispatch({ type: 'edit', payload: response.json });
        } else {
          setStatusErrorMsg(
            `Couldn't update the ${entityName}. Please try again.`
          );
        }
      })
      .catch((error) => {
        setStatusErrorMsg(
          `Couldn't update the ${entityName}. Please try again.`,
          error
        );
      })
      .finally(() => {
        decrementAPIInProgress();
      });
  };

  useEffect(() => {
    if (!entityToEdit) {
      return;
    }

    const phasesFromApi = transformPhasesFromAPIResponse(entityToEdit);
    const defaultDescription =
      entityToEdit.description || entityToEdit.comment || '';
    const defaultLinkUrl = entityToEdit.url || '';

    setDescription(defaultDescription);
    setSelectedPhases(phasesFromApi);
    setOrignialPhases(phasesFromApi);
    setOriginalDescription(defaultDescription);
    setLinkUrl(defaultLinkUrl);
    setOriginalLinkUrl(defaultLinkUrl);
  }, [entityToEdit]);

  return (
    <>
      <Dialog
        aria-describedby="edit-entity-modal-description"
        aria-labelledby="edit-entity-modal-title"
        fullWidth
        maxWidth="lg"
        open={showModal}
        onClose={cancelAction}
      >
        <DialogTitle id="edit-entity-modal-title" sx={{ pb: 0.5 }}>
          <CardHeader
            action={
              <IconButton onClick={cancelAction} aria-label="Close modal">
                <fuel-icon name="close" />
              </IconButton>
            }
            avatar={<fuel-icon name="edit" />}
            title={`Edit ${entityName}`}
            sx={{ p: 0 }}
          />
        </DialogTitle>
        <DialogContent
          id="edit-entity-modal-description"
          sx={{
            '& .MuiFormGroup-root': {
              flexDirection: 'row',
              flexWrap: 'wrap',
              columnGap: 2
            }
          }}
        >
          <MessageBanner
            statusMessages={statusMessages}
            apiInProgressCount={apiInProgressCount}
          />
          <Grid container direction={'column'} spacing={2}>
            {!isCommentEntity && !isLinkEntity && (
              <Grid item>
                <Typography variant="labelMedium">{entityName}</Typography>
                <br></br>
                <Typography variant="bodyMedium">
                  {entityToEdit?.attachment
                    ? entityToEdit.attachment
                    : entityToEdit?.fileName}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
              </Grid>
            )}

            {isLinkEntity && (
              <Grid item>
                <TextField
                  label={LINKS_TITLE}
                  value={linkUrl}
                  fullWidth
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </Grid>
            )}

            <Grid>
              <Typography variant={'labelMedium'}>{TAG_TO_PHASE}</Typography>
              <PhasesTags
                namePrefix={'modal'}
                selectedPhases={selectedPhases}
                setSelectedPhases={setSelectedPhases}
                name={'editPhases'}
              />
            </Grid>

            <Grid>
              <Box>
                <TextField
                  label={isCommentEntity ? COMMENTS_TITLE : 'Description'}
                  value={description}
                  minRows={3}
                  multiline
                  fullWidth
                  inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <Typography
                  variant="labelSmall"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {(description || '').length}/{MAX_DESCRIPTION_LENGTH}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <SMModal
        showModal={showDirtyModal}
        handleCancel={handleDirtyModalCancel}
        handleYes={cancelDirtyOKAction}
        title="Changes Will Be Lost"
        msg_line1="Do you want to save your changes before leaving this screen?"
        msg_line2="If you don't save, your changes will be lost."
        cancelText="Cancel"
        yesText="Continue without Saving"
      />
    </>
  );
}
